<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'

const {
    Icon,
} = UIKit

withDefaults(defineProps<{
    currentStep: number
    isFreeExam?: boolean
    isShowingLicenseFields?: boolean
}>(), {
    isFreeExam: false,
    isShowingLicenseFields: false,
})

const emit = defineEmits<{
    'back': []
}>()

const emitBack = () => {
    emit('back')
}
</script>

<template>
    <div class="upgrade-modal-head">
        <div class="upgrade-modal-head__left">
            <img
                v-if="currentStep === 1"
                class="upgrade-modal-head__logo upgrade-modal-head__logo--desktop"
                src="@/assets/logos/logo.svg"
                alt="Pocket Prep Logo"
            >
            <img
                v-if="currentStep === 1"
                class="upgrade-modal-head__logo upgrade-modal-head__logo--mobile"
                src="@/assets/logos/logo-square.svg"
                alt="Pocket Prep Logo"
            >
            <div
                v-else
                class="upgrade-modal-head__back"
                tabindex="0"
                role="button"
                aria-label="Back"
                @keydown.enter="emitBack"
                @click="emitBack"
                @mousedown.prevent
            >
                <Icon type="arrow" />
            </div>
        </div>
        <div class="upgrade-modal-head__steps">
            <div
                v-if="!isFreeExam"
                class="upgrade-modal-head__step"
                :class="{
                    'upgrade-modal-head__step--active': currentStep === 1,
                    'upgrade-modal-head__step--complete': currentStep > 1,
                }"
                tabindex="-1"
                :aria-label="`${currentStep === 1
                    ? 'Current '
                    : ''
                }Step 1 Choose Plan`"
            >
                <span v-if="currentStep <= 1" aria-hidden="true">1</span>
                <span v-else-if="currentStep > 1" aria-hidden="true"><Icon type="check" /></span>
                <div aria-hidden="true">
                    <div class="upgrade-modal-head__step--desktop">
                        Choose
                    </div> Plan
                </div>
            </div>
            <div
                v-if="!isFreeExam && !isShowingLicenseFields"
                class="upgrade-modal-head__step"
                :class="{ 'upgrade-modal-head__step--active': currentStep === 2 }"
                tabindex="-1"
                :aria-label="`${currentStep === 2
                    ? 'Current '
                    : ''
                }Step 2 Add Payment`"
            >
                <span aria-hidden="true">2</span>
                <div aria-hidden="true">
                    <div class="upgrade-modal-head__step--desktop">
                        Add
                    </div> Payment
                </div>
            </div>
            <div
                v-else-if="!isFreeExam && isShowingLicenseFields"
                class="upgrade-modal-head__step"
                :class="{ 'upgrade-modal-head__step--active': currentStep === 2 }"
                tabindex="-1"
                :aria-label="`${currentStep === 2
                    ? 'Current '
                    : ''
                }Step 2 Add License Code`"
            >
                <span aria-hidden="true">2</span>
                <div aria-hidden="true">
                    <div class="upgrade-modal-head__step--desktop">
                        Add
                    </div> License Code
                </div>
            </div>
        </div>
        <div class="upgrade-modal-head__links" />
    </div>
</template>

<style lang="scss" scoped>
.upgrade-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &__logo {
        height: 27px;

        &--desktop {
            @include breakpoint(black-bear) {
                display: none;
            }
        }

        &--mobile {
            display: none;

            @include breakpoint(black-bear) {
                display: block;
            }
        }
    }

    &__steps {
        display: flex;
    }

    &__step {
        font-size: 13px;
        line-height: 21px;
        color: $slate-01;
        margin-right: 32px;
        position: relative;

        @include breakpoint(black-bear) {
            margin-right: 24px;
        }

        &::after {
            content: '';
            right: -19px;
            top: 6px;
            width: 5px;
            height: 8px;
            position: absolute;
            background: url('@/assets/onboarding/stepper.svg') no-repeat center center;

            @include breakpoint(black-bear) {
                right: -15px;
            }
        }

        &:last-child {
            margin-right: 0;

            &::after {
                display: none;
            }
        }

        &--desktop {
            display: inline;

            @include breakpoint(black-bear) {
                display: none !important;
            }
        }

        span {
            font-weight: 700;
            width: 16px;
            margin: 0 0 0 2px;
            display: inline-block;
        }

        div {
            display: inline-block;
        }

        &--active {
            color: $brand-black;
            font-weight: 600;

            span {
                color: $brand-blue;
            }
        }

        &--complete {
            span {
                margin: 0 2px 0 0;

                svg {
                    width: 16px;
                    height: 13px;
                }
            }

            div {
                text-decoration: line-through;
            }
        }
    }

    &__left {
        display: flex;
        width: 190px;

        @include breakpoint(brown-bear) {
            width: 136px;
        }

        @include breakpoint(black-bear) {
            width: 60px;
        }
    }

    &__back {
        display: flex;
        color: $brand-blue;
        cursor: pointer;
        outline: none;
        position: relative;

        &:hover,
        &:focus {
            color: $brand-black;
        }

        &:focus::before {
            content: "";
            border: 1px solid $brand-black;
            width: 27px;
            height: 26px;
            top: -6px;
            left: -4px;
            border-radius: 4px;
            box-sizing: border-box;
            position: absolute;
        }

        svg {
            transform: rotate(180deg);
            width: 19px;
            height: 14px;
        }
    }

    &__links {
        font-size: 14px;
        line-height: 19px;
        width: 190px;
        text-align: right;
        position: relative;
        right: 76px;

        @include breakpoint(brown-bear) {
            width: 136px;
        }

        @include breakpoint(black-bear) {
            width: 60px;
        }

        span {
            @include breakpoint(brown-bear) {
                display: none;
            }
        }
    }
}
</style>